import { useMemo } from "react";
import { chainOf, layoutGraph, measureText, type GraphView, type PlacedEdge } from "./graphLayout.ts";
import { canvasMeasurer } from "./measure.ts";

const FONT = "13px system-ui, sans-serif";

export const STATE_LABEL = { known: "已掌握", learning: "正在学习", weak: "薄弱", ready: "准备完成", untested: "未测试" } as const;

function curve(e: PlacedEdge): string {
	if (e.kind === "side") return `M ${e.x1} ${e.y1} L ${e.x2} ${e.y2}`;
	if (e.kind === "arc") return `M ${e.x1} ${e.y1} C ${e.x1} ${e.y1 - 20} ${e.x2} ${e.y2 - 20} ${e.x2} ${e.y2}`;
	const bend = Math.max(18, Math.abs(e.y2 - e.y1) / 2) * (e.kind === "up" ? -1 : 1);
	return `M ${e.x1} ${e.y1} C ${e.x1} ${e.y1 + bend} ${e.x2} ${e.y2 - bend} ${e.x2} ${e.y2}`;
}

/**
 * The knowledge graph drawn in stage bands. A selected concept lights its whole chain, what it rests
 * on and what rests on it, and fades the rest; tapping empty space clears the selection.
 */
export function KnowledgeGraph({ view, width, selected, onSelect }: { view: GraphView; width: number; selected: string | null; onSelect: (slug: string | null) => void }) {
	const measure = useMemo(() => canvasMeasurer(FONT, measureText), []);
	const layout = useMemo(() => layoutGraph(view, width, measure), [view, width, measure]);
	const chain = useMemo(() => (selected ? chainOf(view, selected) : null), [view, selected]);

	return (
		<svg className="kg-svg" width={layout.width} height={layout.height} viewBox={`0 0 ${layout.width} ${layout.height}`} role="group" aria-label="知识图谱" onClick={() => onSelect(null)}>
			<defs>
				<marker id="kg-arrow" viewBox="0 0 6 6" refX="5" refY="3" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
					<path d="M0,0 L6,3 L0,6 z" className="kg-arrow" />
				</marker>
			</defs>
			{layout.bands.map((band) => (
				<g key={band.id} className={`kg-band ${view.bands.find((b) => b.id === band.id)?.status ?? "pre"}`}>
					<rect x={0} y={band.y} width={layout.width} height={band.h} />
					<text className="kg-band-name" x={12} y={band.y + 18}>
						{band.label}
						{band.status && <tspan className="kg-band-status" dx={8}>{band.status}</tspan>}
					</text>
					<text className="kg-band-count" x={layout.width - 12} y={band.y + 18} textAnchor="end">
						{band.count}
					</text>
				</g>
			))}
			{layout.edges.map((e) => {
				const lit = chain ? chain.has(e.from) && chain.has(e.to) : false;
				return <path key={`${e.from}>${e.to}`} className={`kg-edge${lit ? " lit" : chain ? " faded" : ""}`} d={curve(e)} markerEnd="url(#kg-arrow)" />;
			})}
			{layout.nodes.map((n) => (
				<g
					key={n.slug}
					data-slug={n.slug}
					className={`kg-node ${n.state}${selected === n.slug ? " selected" : ""}${chain && !chain.has(n.slug) ? " faded" : ""}`}
					role="button"
					tabIndex={0}
					aria-label={`${n.label}，${STATE_LABEL[n.state]}`}
					onClick={(ev) => (ev.stopPropagation(), onSelect(n.slug))}
					onKeyDown={(ev) => (ev.key === "Enter" || ev.key === " ") && (ev.preventDefault(), onSelect(n.slug))}
				>
					{selected === n.slug && <rect className="kg-ring" x={n.x - 4} y={n.y - 4} width={n.w + 8} height={n.h + 8} rx={(n.h + 8) / 2} />}
					<rect x={n.x} y={n.y} width={n.w} height={n.h} rx={n.h / 2} />
					<text x={n.x + n.w / 2} y={n.y + n.h / 2 + 4.5} textAnchor="middle">
						{n.label}
					</text>
				</g>
			))}
		</svg>
	);
}
